import { getCsrfToken } from './auth';

/**
 * Silent login into Ask Paul — the separate Orcanos AI product, opened in a new tab with
 * this session's Orcanos identity already carried across.
 *
 * Written once and shared by the header button and the portal tile, because the awkward
 * part is not the fetch — it is the window.
 *
 * THE TAB IS OPENED SYNCHRONOUSLY, BEFORE THE AWAIT. A browser only treats `window.open`
 * as user-initiated while it is still inside the click handler's own turn; opening it after
 * the token round-trip resolves gets it silently swallowed by the popup blocker, which
 * presents as "the Ask Paul button does nothing" on exactly the machines where blocking is
 * strictest. So we claim the tab first, park it on `about:blank`, and set its location once
 * the one-time link arrives — or close it again if minting failed.
 *
 * The token is never stored and never logged: it lives 60 seconds, is good for one login,
 * and the URL bar is the only place it lands.
 *
 * @param {(msg: string) => void} [onError] told why it failed; falls back to alert().
 * @returns {Promise<boolean>} true when the tab was handed a link.
 */
export async function openAskPaul(onError) {
  const fail = (msg) => {
    if (onError) onError(msg);
    else window.alert(msg);
    return false;
  };

  // NO 'noopener' HERE. Passing it makes window.open return **null** by design — the browser
  // withholds the handle precisely so the opener cannot touch the new window — and then there
  // is nothing to navigate, so the tab sits on about:blank forever. (Shipped exactly that on
  // 2026-08-29: a blank tab, no error, nothing in the console.) The safety `noopener` normally
  // buys is restored below by nulling `opener` ourselves once the location is set.
  const tab = window.open('', '_blank');

  try {
    const resp = await fetch('/api/auth/ask-paul-sso', {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-Token': getCsrfToken() },
    });
    let data = {};
    try { data = await resp.json(); } catch (_) { /* a non-JSON error body is still an error */ }

    if (!resp.ok || !data.url) {
      if (tab) tab.close();
      return fail(data.detail || `Could not open Ask Paul (HTTP ${resp.status}).`);
    }

    if (tab) {
      tab.location.href = data.url;
      // Sever the back-reference by hand, which is what `noopener` would have done if it
      // hadn't also taken the handle away. Wrapped: once the tab is on another origin,
      // touching it can throw, and a cross-origin error here must not read as a failure
      // to open — the tab is already on its way.
      try { tab.opener = null; } catch (_) { /* already cross-origin: nothing to sever */ }
    } else {
      // The popup blocker won the race — fall back to this tab rather than dropping a link
      // the user asked for. The session here survives; they can come back.
      window.location.href = data.url;
    }
    return true;
  } catch (e) {
    if (tab) tab.close();
    return fail('Could not reach the server to open Ask Paul.');
  }
}

export default openAskPaul;
